import Promise from 'bluebird';
import React from 'react';
import { HelpIcon } from '@bingads-webui-campaign-react/help';
import { extend, isEmpty } from 'underscore';
import { endsWith } from 'underscore.string';
import { AdCustomizerBaseAnalyzer } from '../base-analyzer';
import { evaluatelocationRules, parseRule } from './parser';
import LocationEditor from './editor';

const pickerLocationInsert = '{LOCATION(CITY)}';
const pickerLocationInsertCursorPosition = pickerLocationInsert.length - 1;
export class AdCustomizerLocationAnalyzer extends AdCustomizerBaseAnalyzer {
  constructor({ deps: { i18n, instrumentation }, controller }) {
    super({
      name: 'location',
      priority: 3000,
      i18n,
      activityName: 'location',
      instrumentation,
      controller,
    });
  }

  /* return a reactjs object here */
  getSyntax(analyzeResult) {
    if (!analyzeResult.options) return null;
    const locationText = `LOCATION(${this.i18n.getString('Location_Level')})`;
    const syntax = `${this.i18n.getString('AdCustomizers_Editor_Syntax_Prefix_Label')} {${locationText}:${this.i18n.getString('KeywordEditor_DefaultText')}}`;
    return (
      <div>
        <span
          className="locationEditor-syntax"
        >{syntax}
        </span>
        <HelpIcon
          topic="pop_BA_NewAdCreate_LocationInsert_Syntax"
          ariaLabel={this.i18n.getString('Click to learn more about {{item}}', { item: this.i18n.getString('AdCustomizer_Syntax_Syntax') })}
        />
      </div>);
  }

  // get menu item
  getMenuItem({
    startIndex, endIndex, updateAndCloseCallback, target,
  }) {
    return {
      name: this.i18n.getString('Picker_Location_Label'),
      description: this.i18n.getString('Picker_Location_Description'),
      onClick: () => {
        const beginning = target.value.substring(0, startIndex);
        const end = target.value.substring(endIndex);
        updateAndCloseCallback({
          newValue: `${beginning}${pickerLocationInsert}${end}`,
          cursorPosition: beginning.length + pickerLocationInsertCursorPosition,
          isSelectedByUser: true,
        });
      },
    };
  }

  needEditor(analyzeResult) {
    const { options } = analyzeResult || {};
    return !isEmpty(options);
  }

  getEditor({ analyzeResult, updateAndCloseCallback }) {
    const { options } = analyzeResult || {};

    return (<LocationEditor
      i18n={this.i18n}
      options={options}
      updateAndCloseCallback={updateAndCloseCallback}
    />);
  }

  analyzeInput({
    text, cursorIndex, updateAndCloseCallback, target,
  }) {
    const rule = parseRule(text, cursorIndex);

    if (rule) {
      const { options: { startIndex } } = rule;
      return Promise.resolve(extend(
        {
          match: true,
          type: this.name,
          defaultHandlerForInput: false,
          itemsToDisplay: [this.getMenuItem({
            startIndex, endIndex: cursorIndex, updateAndCloseCallback, target,
          })],
        },
        rule
      ));
    }
    // when not match, see whether to show menu item
    if (text && endsWith(text, '{', cursorIndex)) {
      return Promise.resolve({
        type: this.name,
        defaultHandlerForInput: false,
        itemsToDisplay: [this.getMenuItem({
          startIndex: text.length - 1,
          endIndex: cursorIndex,
          updateAndCloseCallback,
          target,
        })],
      });
    }
    return Promise.resolve(null);
  }

  evaluateRule(text) {
    return evaluatelocationRules(text);
  }

  getCharCountExcludingAdCustomizers(text) {
    let charCount = 0;
    let processedText = text || '';

    let tokens = /\{location\((city|state|country)\)}/i.exec(processedText);
    if (tokens) {
      processedText = processedText.replace(tokens[0], '');
      charCount = 10;
    } else {
      tokens = /\{location\((city|state|country)\):([^{}]*)}/i.exec(processedText);
      if (tokens) {
        processedText = processedText.replace(tokens[0], '');
        charCount += tokens[2] ? tokens[2].length : 0;
      }
    }

    return {
      processedText,
      charCount,
    };
  }
}
